import { create } from "zustand";
import { persist } from "zustand/middleware";

interface RoleSummary {
  id: string;
  name: string;
  description?: string;
  actions: {
    id: string;
    key: string;
    description: string;
  }[];
}

export interface AuthUser {
  id: string;
  email: string;
  displayName?: string;
  avatarUrl?: string;
  roles: RoleSummary[];
  permissions: string[];
  lastLoginAt?: string;
  createdAt?: string;
}

interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  hasHydrated: boolean;
  login: (email: string, password: string, rememberMe?: boolean) => Promise<void>;
  logout: () => Promise<void>;
  hydrate: () => Promise<void>;
  setUser: (user: AuthUser | null) => void;
  hasPermission: (permission: string) => boolean;
  updateProfile: (name: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const API_BASE = import.meta.env.VITE_API_URL || "";

const buildUrl = (path: string) =>
  path.startsWith("http") ? path : `${API_BASE}${path}`;

async function request<T>(
  path: string,
  options: RequestInit = {}
): Promise<T> {
  const response = await fetch(buildUrl(path), {
    method: options.method || "GET",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    credentials: "include",
    body: options.body,
  });

  if (response.status === 204) {
    return {} as T;
  }

  const data = await response.json().catch(() => ({}));

  if (!response.ok) {
    const message =
      (data && (data.message || data.error)) || "Request failed";
    throw new Error(message);
  }

  return data as T;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      hasHydrated: false,

      setUser: (user: AuthUser | null) => {
        set({
          user,
          isAuthenticated: Boolean(user),
        });
      },

      hasPermission: (permission: string) => {
        const current = get().user;
        const permissions = current?.permissions;
        if (!permissions || !Array.isArray(permissions)) {
          return false;
        }
        return permissions.includes(permission);
      },

      login: async (email: string, password: string, rememberMe = true) => {
        const data = await request<{ user: AuthUser }>(
          "/api/v1/auth/sign-in",
          {
            method: "POST",
            body: JSON.stringify({ email, password, rememberMe }),
          }
        );

        set({
          user: data.user,
          isAuthenticated: true,
          hasHydrated: true,
        });
      },

      logout: async () => {
        try {
          await request("/api/v1/auth/sign-out", {
            method: "POST",
          });
        } finally {
          set({
            user: null,
            isAuthenticated: false,
          });
        }
      },

      hydrate: async () => {
        // Race the API call against an 8-second timeout so the UI never hangs forever
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error("hydrate_timeout")), 8000)
        );
        try {
          const data = await Promise.race([
            request<{ user: AuthUser }>("/api/v1/auth/me"),
            timeoutPromise,
          ]);
          set({ user: data.user, isAuthenticated: true, hasHydrated: true });
        } catch (err) {
          if (err instanceof Error && err.message === "hydrate_timeout") {
            // Server too slow — unblock the UI and keep whatever persisted state we have
            set({ hasHydrated: true });
          } else {
            set({ user: null, isAuthenticated: false, hasHydrated: true });
          }
        }
      },

      updateProfile: async (name: string) => {
        const data = await request<{ user: AuthUser }>(
          "/api/v1/auth/users/me/profile",
          {
            method: "PATCH",
            body: JSON.stringify({ name }),
          }
        );
        set({
          user: data.user,
          isAuthenticated: true,
        });
      },

      changePassword: async (currentPassword: string, newPassword: string) => {
        await request<{ success?: boolean }>(
          "/api/v1/auth/users/me/password",
          {
            method: "PATCH",
            body: JSON.stringify({ currentPassword, newPassword }),
          }
        );
      },
    }),
    {
      name: "auth-storage",
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Optimistic: if we have a persisted authenticated user, show the app
          // immediately and let hydrate() verify the session in the background.
          // If the session expired, hydrate() will set isAuthenticated=false → redirect to login.
          state.hasHydrated = Boolean(state.isAuthenticated && state.user);
        }
      },
    }
  )
);
